/**
 * AAAprinters - User Statistics Hook
 * Calculates user profile statistics and activity metrics
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

export interface UserStats {
  totalOrders: number;
  totalSpent: number;
  activeOrders: number;
  completedOrders: number;
  savedDesigns: number;
  favoriteDesigns: number;
  savedAddresses: number;
  wishlistItems: number;
  memberSince: string;
  lastOrderDate: string | null;
  averageOrderValue: number;
}

interface UseUserStatsReturn {
  stats: UserStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useUserStats(): UseUserStatsReturn {
  const { user } = useAuth();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!user) {
      setStats(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel for better performance
      const [
        ordersResult,
        designsResult,
        addressesResult,
        wishlistResult,
        profileResult
      ] = await Promise.all([
        supabase
          .from('orders')
          .select('status, total_amount, created_at')
          .eq('user_id', user.id),
        supabase
          .from('user_designs')
          .select('is_favorite')
          .eq('user_id', user.id),
        supabase
          .from('addresses')
          .select('id')
          .eq('user_id', user.id),
        supabase
          .from('wishlist_items')
          .select('id')
          .eq('user_id', user.id),
        supabase
          .from('profiles')
          .select('created_at')
          .eq('id', user.id)
          .single()
      ]);

      // Process orders data
      const orders = ordersResult.data || [];
      const totalOrders = orders.length;
      const totalSpent = orders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
      const activeOrders = orders.filter(o =>
        ['pending', 'confirmed', 'processing', 'shipped'].includes(o.status)
      ).length;
      const completedOrders = orders.filter(o => o.status === 'delivered').length;

      // Find last order date
      const lastOrderDate = orders.length > 0
        ? orders.reduce((latest, order) => {
            const orderDate = new Date(order.created_at);
            return orderDate > latest ? orderDate : latest;
          }, new Date(orders[0].created_at)).toISOString()
        : null;

      // Calculate average order value
      const averageOrderValue = totalOrders > 0 ? totalSpent / totalOrders : 0;

      // Process designs data
      const designs = designsResult.data || [];
      const savedDesigns = designs.length;
      const favoriteDesigns = designs.filter(d => d.is_favorite).length;

      // Process addresses data
      const savedAddresses = (addressesResult.data || []).length;

      // Process wishlist data
      const wishlistItems = (wishlistResult.data || []).length;

      // Member since date
      const memberSince = profileResult.data?.created_at || user.created_at || new Date().toISOString();

      const calculatedStats: UserStats = {
        totalOrders,
        totalSpent,
        activeOrders,
        completedOrders,
        savedDesigns,
        favoriteDesigns,
        savedAddresses,
        wishlistItems,
        memberSince,
        lastOrderDate,
        averageOrderValue
      };

      setStats(calculatedStats);
    } catch (err) {
      console.error('Error fetching user stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user statistics');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  };
}
